import { parseTimestampsInResponse } from "@/services/utils";
import { MonitoringEvaluator, parseMonitoringEvaluatorResponse } from "@/services/files.service";
import { FileFromResponse, FileResponse } from "./file";
import { FileRequestIdentifiers, ToolChoice, AgentLogResponse } from "./log";
import {
  BaseLinkedFileRequest,
  LinkedFileRequest,
  parsePromptTools,
  PromptKernelRequest,
  PromptResponse,
  PromptToolRequest,
} from "./prompt";
import { parseEnvironmentResponse } from "./environment";
import { ChatMessage } from "@/services/promptEditor.service";
import { ProviderApiKeys } from "./generate";
import { ToolFunction } from "./tool";
import { File } from "./file";

type OnAgentCall = "continue" | "stop";

interface BaseAgentTool {
  on_agent_call: OnAgentCall;
}

export interface AgentLinkedFileRequest extends BaseAgentTool, BaseLinkedFileRequest<"file"> {
  type: "file";
  link: LinkedFileRequest;
}

export interface AgentLinkedFileResponse extends AgentLinkedFileRequest {
  file: FileResponse;
}

export interface AgentInlineTool extends BaseAgentTool, BaseLinkedFileRequest<"inline"> {
  type: "inline";
  json_schema: ToolFunction;
}

export type AgentToolRequest = AgentLinkedFileRequest | AgentInlineTool;

type AgentToolResponse = AgentLinkedFileResponse | AgentInlineTool;

export const isAgentToolRequest = (tool: AgentToolRequest | PromptToolRequest): tool is AgentToolRequest => {
  return "on_agent_call" in tool;
};

export interface AgentKernelRequest extends Omit<PromptKernelRequest, "tools" | "linked_tools"> {
  tools?: AgentToolRequest[];
  max_iterations?: number | null;
}

export interface AgentRequest extends AgentKernelRequest, FileRequestIdentifiers {
  version_name?: string | null;
  version_description?: string | null;
  description?: string | null;
  tags?: string[];
  readme?: string | null;
}

export interface AgentResponse extends Omit<PromptResponse, "type" | "tools" | "linked_tools"> {
  type: "agent";
  tools: AgentToolResponse[];
}

export interface AgentCallRequest {
  id: string;
  path?: string;
  agent?: AgentKernelRequest;
  messages?: ChatMessage[];
  tool_choice?: "none" | "auto" | "required" | ToolChoice;
  inputs?: Record<string, unknown>;
  source?: string;
  metadata?: Record<string, unknown>;
  start_time?: string;
  end_time?: string;
  log_status?: "pending" | "complete" | "failed";
  source_datapoint_id?: string;
  trace_parent_id?: string;
  user?: string;
  environment?: string;
  save?: boolean;
  log_id?: string;
  provider_api_keys?: ProviderApiKeys;
  num_samples?: number;
  stream?: boolean;
  return_inputs?: boolean;
  logprobs?: number;
  suffix?: string;
}

export interface AgentCallResponse extends AgentLogResponse {
  /** The previous agent message, if the agent is suspended and can continue. */
  previous_agent_message: ChatMessage | null;
}

/**
 * Response model for calling Agent in streaming mode.
 */
export interface AgentCallStreamResponse {
  /** Generated output from your model for the provided inputs. Can be `None` if logging an error, or if creating a parent Log with the intention to populate it later. */
  output?: string;
  /** User defined timestamp for when the log was created. */
  created_at?: Date;
  /** Error message if the log is an error. */
  error?: string;
  /** Duration of the logged event in seconds. */
  provider_latency?: number;
  /** Captured log and debug statements. */
  stdout?: string;
  /** The message returned by the provider. */
  output_message?: ChatMessage;
  /** Number of tokens in the prompt used to generate the output. */
  prompt_tokens?: number;
  /** Number of tokens in the output generated by the model. */
  output_tokens?: number;
  /** Cost in dollars associated to the tokens in the prompt. */
  prompt_cost?: number;
  /** Cost in dollars associated to the tokens in the output. */
  output_cost?: number;
  /** Reason the generation finished. */
  finish_reason?: string;
  /** The index of the sample in the batch. */
  index: number;
  /** ID of the log. */
  id: string;
  /** ID of the Agent the log belongs to. */
  prompt_id: string;
  /** ID of the specific version of the Agent. */
  version_id: string;
}

/**
 * Request model for continuing an Agent call at /agents/continue.
 */
export interface AgentContinueRequest {
  /** The ID of the Agent Log to continue. */
  log_id: string;
  /** The additional messages with which to continue the Agent Log. */
  messages: ChatMessage[];
  /** API key overrides. */
  provider_api_keys?: ProviderApiKeys;
  /** Whether to stream the response. */
  stream?: boolean;
}

export type AgentTool = (Omit<AgentLinkedFileResponse, "file"> & { file: File }) | AgentInlineTool;

export interface Agent extends Omit<FileFromResponse<AgentResponse>, "evaluators" | "tools"> {
  tools: AgentTool[];
  evaluators: MonitoringEvaluator[] | null;
  environments: any[];
}

export const parseAgentResponse = (response: AgentResponse): Agent => {
  return {
    ...response,
    ...parseTimestampsInResponse(response, ["created_at", "updated_at", "committed_at", "last_used_at"]),
    environments: response.environments.map(parseEnvironmentResponse),
    evaluators: response.evaluators
      ? response.evaluators.map((evaluator) => parseMonitoringEvaluatorResponse(evaluator))
      : null,
    tools: parsePromptTools<AgentResponse, Agent>(response.tools),
  };
};
